import { useEffect, useState } from "react";
import { onSnapshot, orderBy, query } from "firebase/firestore";
import {
  artifactVersionsCollection,
  dataManagementVersionsCollection,
  requirementsVersionsCollection,
  type DataManagementDocId,
  type RequirementsDocId,
} from "../constants/firestorePaths";
import type { IssuedVersion } from "../types/issuedVersion";

export type SourceDocFamily = "artifact" | "dataManagement" | "requirements";

export interface UseIssuedVersionsArgs {
  projectId: string;
  sourceDocFamily: SourceDocFamily;
  sourceDocId: string;
}

export interface UseIssuedVersionsResult {
  versions: IssuedVersion[];
  loading: boolean;
  error: Error | null;
}

/**
 * Real-time list of issued versions for a single source document, sorted
 * newest-first by `issuedAt`. Used by the History expandable on the
 * Documents & TMF page and on each editor's banner.
 */
export function useIssuedVersions(
  args: UseIssuedVersionsArgs,
): UseIssuedVersionsResult {
  const { projectId, sourceDocFamily, sourceDocId } = args;
  const [versions, setVersions] = useState<IssuedVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !sourceDocId) {
      setVersions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const ref = (() => {
      if (sourceDocFamily === "artifact") {
        return artifactVersionsCollection(projectId, sourceDocId);
      }
      if (sourceDocFamily === "dataManagement") {
        return dataManagementVersionsCollection(
          projectId,
          sourceDocId as DataManagementDocId,
        );
      }
      return requirementsVersionsCollection(
        projectId,
        sourceDocId as RequirementsDocId,
      );
    })();

    const unsubscribe = onSnapshot(
      query(ref, orderBy("issuedAt", "desc")),
      (snap) => {
        const items: IssuedVersion[] = snap.docs.map(
          (d) => ({ id: d.id, ...d.data() } as IssuedVersion),
        );
        setVersions(items);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [projectId, sourceDocFamily, sourceDocId]);

  return { versions, loading, error };
}
